import axios from "axios";
import { showToast } from "../utils/styles/toastUtils";


// Prevent infinite redirect loops during logout
let _logoutInProgress = false;

function performLogout(message) {
  if (_logoutInProgress) return; // Already logging out
  _logoutInProgress = true;

  localStorage.clear();
  document.documentElement.setAttribute("data-theme", "light");
  sessionStorage.setItem("sessionExpired", message);
  window.location.href = "/";
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If logout is already in progress, don't process further errors
    if (_logoutInProgress) {
      return Promise.reject(error);
    }

    // Globally catch structural/network errors so they have a baseline handling
    if (!error.response) {
      // Network error (CORS, connection refused, timeout, etc.)
      const isCorsError = error.message.includes('cors') ||
        error.message.includes('Cross-Origin') ||
        error.message.includes('401');

      if (isCorsError || error.code === 'ERR_NETWORK') {
        console.error("Network/CORS Error - Backend may be offline or CORS misconfigured:", error.message);
      } else {
        console.error("Network Error:", error.message);
      }
    } else if (error.response.status >= 500) {
      console.error("Server Error:", error.response.status);
    }
    return Promise.reject(error);
  }
);

export const BASE_URL = "https://api.witter24.com/api/v1/";
// export const BASE_URL = "http://18.207.167.30:3000/api/v1/";
// export const BASE_URL = "https://unstampeded-nereida-unwishfully.ngrok-free.dev/api/v1/";
// export const BASE_URL = "https://crevice-cornflake-haphazard.ngrok-free.dev/api/v1/";

const STATUS_CODE = {
  INTERNAL_SERVER_ERROR: 422,
  SUCCESS: 200,
};

const METHOD = {
  GET: "get",
  POST: "post",
  PUT: "put",
  PATCH: "patch",
  DELETE: "delete",
};

// ─── Session-expiry deduplication ────────────────────────────────────────────
// Multiple simultaneous 401 responses (e.g. parallel API calls when a token is
// invalidated after admin approval) must only trigger ONE cleanup+redirect cycle.
// This module-level flag is the gate; it resets naturally on full page reload.
let _sessionExpiredHandled = false;

function handleSessionExpiry(error) {
  console.warn("🔴 [SESSION EXPIRY] Session expired - redirecting to login");
  const hadToken = !!localStorage.getItem("token");

  if (!hadToken) {
    console.warn("🔴 [SESSION EXPIRY] No token found, skipping redirect");
    return;
  }

  if (_sessionExpiredHandled) {
    console.warn("🔴 [SESSION EXPIRY] Already handled, skipping");
    return;
  }
  _sessionExpiredHandled = true;

  if (hadToken) {
    // Extract server message if available (e.g. "Session expired. Please log in again.")
    const serverMsg = error?.response?.data?.errors;
    const message =
      (typeof serverMsg === "string" && serverMsg.trim()) ||
      "Your session has expired. Please log in again.";

    console.warn("🔴 [SESSION EXPIRY] Clearing storage and redirecting to home");
    // Store in sessionStorage so the Login page can display it after the reload.
    // (localStorage is about to be cleared; sessionStorage survives the redirect.)
    sessionStorage.setItem("sessionExpired", message);

    localStorage.clear();
    document.documentElement.setAttribute("data-theme", "light");
    window.location.href = "/";
  }
}

class API {
  constructor() {
    this.baseURL = BASE_URL;
    // Create axios instance for this API object
    this._axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      withCredentials: false,
    });

    // Add request interceptor to include JWT token in ALL requests
    this._axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
         
        } else {
          console.log("No token found in localStorage for request to", config.url);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /* ------------------ TOAST HELPERS ------------------ */

  handleSuccess(_response) {
    // Success toasts are disabled; hook here if needed in future.
  }

  handleError(error) {
    // ── 401 Token Version Mismatch ────────────────────────────────────────────
    // User account was upgraded (user→business), old token is now invalid
    if (
      error.response?.status === 401 &&
      error.response?.data?.token_version_mismatch === true
    ) {
      console.warn("🔴 [TOKEN VERSION MISMATCH] Account upgraded - clearing old token");
      performLogout("Your account was updated. Please log in again to continue.");
      throw error;
    }

    // ── 401 User Deleted ─────────────────────────────────────────────────────
    // Account deleted by admin — user still has token but account is gone
    if (
      error.response?.status === 401 &&
      error.response?.data?.user_deleted === true
    ) {
      console.warn("🔴 [USER DELETED] Account deleted by admin - logging out");
      performLogout("Your account has been deleted by the administrator.");
      throw error;
    }

    // ── 401 Unauthorized / session expired ─────────────────────────────────
    // ONLY logout if backend explicitly says "session_expired: true"
    // This prevents accidental logouts from endpoint-specific 401 errors
    if (error.response?.status === 401 && error.response?.data?.session_expired === true) {
      console.warn("🔴 [SESSION EXPIRED] Token expired or session invalid");
      performLogout("Your session has expired. Please log in again.");
      throw error;
    }

    // ── 403 account_inactive ────────────────────────────────────────────────
    // Account deactivated by admin — different path, NOT a session expiry.
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === "account_inactive"
    ) {
      if (localStorage.getItem("token")) {
        // Mid-session deactivation — clear everything and show the inactive page
        localStorage.clear();
        document.documentElement.setAttribute("data-theme", "light");
        window.location.href = "/account-inactive";
      }
      // Login-time deactivation (no token yet) — suppress toast, let signInAction handle it
      return;
    }

    // ── 503 Maintenance ─────────────────────────────────────────────────────
    if (error.response?.status === 503) {
      window.location.href = "/under-maintenance";
      return;
    }

    // ── Everything else: show a toast ───────────────────────────────────────
    let errorMessage = "Something went wrong! Please try again.";

    if (error.response) {
      const errors = error.response?.data?.errors;
      errorMessage =
        (Array.isArray(errors) ? errors.join(", ") : errors) ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.messages ||
        errorMessage;
    } else if (error.request) {
      errorMessage = "No response from server. Check your internet connection.";
    } else {
      errorMessage = error.message || errorMessage;
    }

    showToast.error(errorMessage);
  }

  /* ------------------ HTTP METHODS ------------------ */

  async post(url, data, options = {}) {
    try {
      const { skipErrorHandling, ...axiosOptions } = options;
      const response = await this.api(METHOD.POST, url, data, axiosOptions);
      this.handleSuccess(response);
      return response;
    } catch (error) {
      if (!options.skipErrorHandling) {
        this.handleError(error);
      }
      throw error;
    }
  }

  async put(url, data, options = {}) {
    try {
      const { skipErrorHandling, ...axiosOptions } = options;
      const response = await this.api(METHOD.PUT, url, data, axiosOptions);
      this.handleSuccess(response);
      return response;
    } catch (error) {
      if (!options.skipErrorHandling) {
        this.handleError(error);
      }
      throw error;
    }
  }

  async patch(url, data, options = {}) {
    try {
      const { skipErrorHandling, ...axiosOptions } = options;
      const response = await this.api(METHOD.PATCH, url, data, axiosOptions);
      this.handleSuccess(response);
      return response;
    } catch (error) {
      if (!options.skipErrorHandling) {
        this.handleError(error);
      }
      throw error;
    }
  }

  async delete(url, options = {}) {
    try {
      const { skipErrorHandling, ...axiosOptions } = options;
      const response = await this.api(METHOD.DELETE, url, null, axiosOptions);
      this.handleSuccess(response);
      return response;
    } catch (error) {
      if (!options.skipErrorHandling) {
        this.handleError(error);
      }
      throw error;
    }
  }

  async get(url, options = {}) {
    try {
      const response = await this.api(METHOD.GET, url, null, options);
      return response;
    } catch (error) {
      if (!options.skipErrorHandling) {
        this.handleError(error);
      }
      throw error;
    }
  }

  /* ------------------ CORE API CALL ------------------ */

  async api(method, url, data, axiosOptions = {}) {
    try {
      const axiosConfig = {
        method,
        url: url,  // Don't include baseURL here, it's in the instance
        headers: await this.setHeaders(data),
        // ngrok-specific: don't use credentials for CORS in development
        withCredentials: false,
        // Increase timeout for ngrok tunnels
        timeout: 30000,
        // Merge in axios-specific options (responseType, etc.)
        ...axiosOptions,
      };

      if (data) {
        axiosConfig.data = data;
      }

      // Use instance with interceptor that adds Authorization header
      const response = await this._axiosInstance(axiosConfig);

      if (response.status === STATUS_CODE.INTERNAL_SERVER_ERROR) {
        throw new Error("Internal Server Error");
      }

      return response;
    } catch (error) {
      // Better error logging for debugging
      if (!error.response) {
        console.error("API Request Failed:", {
          url: this.baseURL + url,
          method: method,
          error: error.message,
          code: error.code,
        });
      }
      throw error;
    }
  }

  /* ------------------ HEADERS ------------------ */

  async setHeaders(data) {
    try {
      const token = localStorage.getItem("token");

      let headers = {
        "accept-language": "en",
        "Content-Type": "application/json",
        Accept: "application/json",
        "ngrok-skip-browser-warning": "true",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      if (data) {
        // If data is FormData, let the browser set the Content-Type with the boundary
        if (data instanceof FormData) {
          delete headers["Content-Type"];
        } else if (data.isMultipart) {
          headers["Content-Type"] = "multipart/form-data";
        }

        if (data.headers) {
          Object.assign(headers, data.headers);
        }
      }

      return headers;
    } catch (error) {
      console.error("Header setup error:", error);
      return {
        "accept-language": "en",
        "Content-Type": "application/json",
        Accept: "application/json",
      };
    }
  }
}

export default API;
